import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Table, Container, Row, Col, Form, Button, Stack, ProgressBar } from "react-bootstrap";
import { useMediaQuery } from "react-responsive";

import supabase from "../utils/Supabase";
import { showToast } from "../utils/Toasts";
import { numberFormatter } from "../utils/Numbers";
import { FilterContext } from "../utils/Contexts";
import { applyFilter } from "../utils/ApplyFilter";
import { getScripts } from "../utils/GetScripts";
import { sortScripts } from '../utils/SortScripts';
import { exportCsv } from "../utils/ExportCsv";
import { getFileMatches } from "../utils/Database";

import {
  fetchPendingRequests,
  fetchAddedRequests,
  deleteRequest,
  checkForDeletedRequests,
  getUserRequestCount
} from "../utils/Requests";

import { SearchText } from "../components/SearchFields";
import PageList from "../components/PageList";
import SortableHeader from "../components/SortableHeader";

import useScriptsStore from "../stores/Scripts";
import userStore from "../stores/User";
import { userHasBenefit } from "../utils/Benefits";

import { LoadingRing } from "../components/LoadingRing";
import { AddConfirmationModal, ConfirmationModal } from "../components/Confirmation";

const ITEMS_PER_PAGE = 50;
const ADDED_ITEMS_PER_PAGE = 10;
const USER_REQUEST_LIMIT = 25;

function RequestsPage() {
  const isSmall = useMediaQuery({ query: '(max-width: 576px)' });
  const isMedium = useMediaQuery({ query: '(min-width: 768px)' });
  const isLarge = useMediaQuery({ query: '(min-width: 992px)' });
  const isExtraLarge = useMediaQuery({ query: '(min-width: 1200px)' });
  const isExtraExtraLarge = useMediaQuery({ query: '(min-width: 1400px)' });

  const userData = userStore((state) => state.data);
  const isLoading = userStore((state) => state.loading);
  const userCanRequest = useRef(false);
  const [totalUserRequests, setTotalUserRequests] = useState(0);
  const totalScripts = useScriptsStore((state) => state.totalScripts);
  const setTotalScripts = useScriptsStore((state) => state.setTotalScripts);
  const totalScriptsCount = useScriptsStore((state) => state.totalCount);
  const setTotalScriptsCount = useScriptsStore((state) => state.setTotalCount);
  const [filteredScriptCount, setFilteredScriptCount] = useState(0);
  const [totalRequests, setTotalRequests] = useState([])
  const [addedRequests, setAddedRequests] = useState([])
  const haveAdded = useRef(false);

  const [filteredRequests, setFilteredRequests] = useState([])
  const [filteredRequestCount, setFilteredRequestCount] = useState(0);
  const [filters, setFilters] = useState({});
  const [haveTitle, setHaveTitle] = useState(false);
  const [haveRequests, setHaveRequests] = useState(false);
  const [scriptsFound, setScriptsFound] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [onDeleteConfirm, setOnDeleteConfirm] = useState(() => () => {});
  const [onDeleteHide, setOnDeleteHide] = useState(() => () => {});

  const [showAddModal, setShowAddModal] = useState(false);
  const [onAddConfirm, setOnAddConfirm] = useState(() => () => {});
  const [onAddHide, setOnAddHide] = useState(() => () => {});

  const [scriptTitle, setScriptTitle] = useState("");
  const [scriptWriters, setScriptWriters] = useState("");
  const [requestID, setRequestID] = useState(null);

  const [shownRequests, setShownRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [shownAddedRequests, setShownAddedRequests] = useState([]);
  const [currentAddedPage, setCurrentAddedPage] = useState(1);
  
  type SortConfig = { key: string; direction: "asc" | "desc" };
  const [sortConfig, setSortConfig] = useState({ key: 'date_requested', direction: 'desc' } as SortConfig);
  const sortedRequests = useMemo(() => sortScripts(totalRequests, sortConfig), [totalRequests, sortConfig]);

  const [canAddDelete, setCanAddDelete] = useState(userData?.isApprover || userData?.isCurator || false);
  // Script requests area used to key off isVIP; now it is a named Patreon feature
  const hasVipAccess = userHasBenefit(userData, "scriptRequests") || canAddDelete;

  const [showCheckMatches, setShowCheckMatches] = useState(false);
  const [isCheckingMatches, setIsCheckingMatches] = useState(false);
  const isCheckingMatchesRef = useRef(false);
  const [matchesFound, setMatchesFound] = useState([]);
  const [matchProgress, setMatchProgress] = useState({ current: 0, total: 0 });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Form data state
  const INITIAL_FORM_VALUES = {
    writers: '',
    additional_info: '',
    requested_email: userData?.email || ''
  };
  const [formData, setFormData] = useState({ ...INITIAL_FORM_VALUES });
  const resetForm = () => {
    setFormData({
      ...INITIAL_FORM_VALUES,
      requested_email: userData?.email || ''
    });
    setFilters({});
    setSearchDone(false);
  };

  useEffect(() => {
    if (totalScriptsCount > 0) {
      return;
    }

    const getScriptCount = async () => {
      const { data, error } = await supabase
        .rpc("scripts_count");

      if (!data || error) {
        console.error("Failed to fetch scripts count:", error);
        return;
      }

      setTotalScriptsCount(data);
    };

    getScriptCount();
  }, [totalScriptsCount, setTotalScriptsCount])

  useEffect(() => {
    if (totalScripts.length === 0 && userData) {
      getScripts().then((data) => {
        data ? setTotalScripts(data as []) : showToast("error", "Unable to get scripts from database!");
      });
    }
  }, [totalScripts.length, setTotalScripts, userData]);

  useEffect(() => {
    const results = applyFilter(totalScripts, filters);
    setFilteredScriptCount(results.length);
    setHaveTitle("Script Title" in filters && filters["Script Title"] !== "");
  }, [totalScripts, filters]);

  useEffect(() => {
    if (filters) {
      const results = applyFilter(
        sortedRequests, {
          script_title: filters["Script Title"] || filters["script_title"],
          writers: filters["writers"],
          additional_info: filters["additional_info"],
          requested_email: filters["requested_email"]
        }
      );
      setFilteredRequests(results);
      setFilteredRequestCount(results.length);
      setCurrentPage(1);
    } else {
      setFilteredRequests(sortedRequests);
      setFilteredRequestCount(sortedRequests.length);
    }
  }, [sortedRequests, filters]);

  useEffect(() => {
    if (userData && sortedRequests.length > 0 && haveAdded.current === false) {
      fetchAddedRequests().then((data) => {
        if (data) {
          const sortedData = sortScripts(data, { key: 'files.Date Added', direction: 'desc' });
          setAddedRequests(sortedData);
          haveAdded.current = true;
        }
      });
    }
  }, [userData, sortedRequests])

  useEffect(() => {
    setScriptsFound(filteredScriptCount !== 0);
  }, [filteredScriptCount]);

  useEffect(() => {
    setSearchDone(false);
  }, [filters]);

  useEffect(() => {
    fetchPendingRequests().then((data) => {
      if (data) {
        setTotalRequests(data);
        setShownRequests(data.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE));
        setHaveRequests(true);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const requests = filters ? filteredRequests : sortedRequests;
    setShownRequests(requests.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE));
  }, [currentPage, sortedRequests, filteredRequests, filters]);

  useEffect(() => {
    if (userData?.email && !formData.requested_email) {
      setFormData(prev => ({
        ...prev,
        requested_email: userData.email
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.email]);

  useEffect(() => {
    setCanAddDelete(userData?.isApprover || userData?.isCurator || false);
  }, [userData]);

  useEffect(() => {
    const fetchUserRequestCount = async () => {
      if (userData?.user_id) {
        const requestCount = await getUserRequestCount(userData.user_id);
        if (requestCount !== undefined) {
          userCanRequest.current = requestCount < USER_REQUEST_LIMIT;
          setTotalUserRequests(requestCount);
        }
      }
    };
    fetchUserRequestCount();
  }, [userData?.user_id, totalRequests]);

  useEffect(() => {
    const requests = addedRequests;
    setShownAddedRequests(requests.slice((currentAddedPage - 1) * ADDED_ITEMS_PER_PAGE, currentAddedPage * ADDED_ITEMS_PER_PAGE));
  }, [currentAddedPage, addedRequests]);

  const fetchMatches = async (requests) => {
    setIsCheckingMatches(true);
    isCheckingMatchesRef.current = true;
    let i = 0;
    for (const request of requests) {
      if (!isCheckingMatchesRef.current) {
        break;
      }
      i++;
      setMatchProgress({ current: i, total: requests.length });
      const matches = await getFileMatches(request.script_title, request.writers);
      if (matches.length > 0) {
        setMatchesFound(prev => ([...prev, { ...request, matches: matches }]));
      }
    }
    setIsCheckingMatches(false);
    isCheckingMatchesRef.current = false;
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const scriptTitle = filters["Script Title"];
      const writers = formData.writers.trim();
      
      if (!scriptTitle || typeof scriptTitle !== 'string') {
        showToast("error", "Script title is required");
        setIsSubmitting(false);
        return;
      }
      
      // Check for previously deleted requests
      const wasDeleted = await checkForDeletedRequests(scriptTitle, writers);
      if (wasDeleted) {
        showToast("error", "A request for this script was previously removed and cannot be resubmitted.", 5000);
        setIsSubmitting(false);
        resetForm();
        return;
      }

      const submissionData = {
        ...formData,
        script_title: filters["Script Title"].trim(),
        date_requested: new Date().toISOString(),
        requested_id: userData.user_id || ''
      };

      const { error: insertError } = await supabase
        .from("requests")
        .insert([submissionData]);

      if (insertError) {
        showToast("error", `Sorry, I was unable to add your request: ${insertError.message}`);
      } else {
        resetForm();

        fetchPendingRequests().then((data) => {
          if (data) {
            setTotalRequests(data);
          }
        });

        showToast("info", "Submitted! You currently have " + (totalUserRequests + 1) + " pending request(s). To view or delete them, go to your account page.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      showToast("error", "An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const shouldShowForm = hasVipAccess && haveTitle && (
    // Show form if user has confirmed they want to submit after finding scripts/requests
    searchDone ||
    // Show form if no scripts AND no existing requests for this title
    (!scriptsFound && filteredRequestCount === 0)
  );

  return (
    <>
      {!showCheckMatches ? (
        <div className="mx-1 mx-sm-5">
          <header className="mt-4">
            <Container className="mw-100">
              <Row>
                <Col><h3 className="font-bold">Request a Script</h3></Col>
              </Row>
                <Row>
                  <Col>
                    <p>
                      {
                        userData ?
                          hasVipAccess ? (
                            userCanRequest.current ? "Looking for a script that we don't currently have in our database? Submit a request!" : ''
                          ) : (
                            <>
                              {"Script requests require volunteer research. Limiting requests to "}
                              <Link to="https://patreon.com/scripthive" target="_blank">Patrons</Link>
                              {" helps support the volunteers who spend their time tracking them down."}
                            </>
                          )
                            : isLoading ? (
                                <></>
                              ) : (
                                <>
                                  {"Looking for a specific a script? "}
                                  <Link to={{ pathname: "/register" }}>Register</Link>
                                  {" or "}
                                  <Link to={{ pathname: "/DatabaseLogin" }}>log in</Link>
                                  {" to search our "}
                                  <Link to={{ pathname: "/database" }}>database</Link>
                                  {" of "}
                                  <strong>{totalScriptsCount > 0 ? numberFormatter(totalScriptsCount) : ''}</strong>
                                  {" scripts first to see if we already have it available!"}
                                </>
                              )
                      }
                    </p>
                  </Col>
                </Row>
              </Container>
          </header>

          <main>
            {haveRequests ? (
              <>
                {userData && hasVipAccess && (
                  <Container className="mw-100">
                    {userCanRequest.current ? (
                      <>
                        {totalUserRequests && totalUserRequests !== 0 ? (
                          <Row>
                            <Col>
                              <p>You currently have <strong>{totalUserRequests}</strong> pending request{totalUserRequests !== 1 ? 's' : ''}. To check the status of your request{totalUserRequests !== 1 ? 's' : ''}, visit your <Link to="/account">account page</Link>.</p>
                            </Col>
                          </Row>
                        ) : null}
                        <Row>
                          <Col md={12} lg={6} xxl={4}>
                            <FilterContext value={{ filters: filters, setFilters: setFilters, search: {} }}>
                              <SearchText
                                fieldName="Script Title"
                                disabled={filters && (filters["script_title"] || filters["writers"] || filters["additional_info"])}
                              />
                            </FilterContext>
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <Row>
                        <Col>
                            {totalUserRequests && totalUserRequests !== 0 ? (
                              <p>Sorry, you have <strong className="text-danger">{totalUserRequests}</strong> pending requests and the limit is <strong>{USER_REQUEST_LIMIT}</strong>. To request something new, go to your <Link to="/account">account page</Link> and delete requests.</p>
                            ) : null}
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col>
                        {haveTitle && (
                          <div className="mt-2">
                            <p>
                              {scriptsFound ?
                                <>
                                  {`There ${filteredScriptCount === 1 ? "is" : "are"} `}
                                  <strong>{numberFormatter(filteredScriptCount)}</strong>
                                </>
                                : "There aren't any"}
                              {` ${filteredScriptCount === 1 ? "script" : "scripts"} in the database containing that title.`}
                              {scriptsFound && filters["Script Title"] && (
                                <span>
                                  {" Try "}
                                  <Link to={{
                                    pathname: "/database",
                                    search: "?Script+Title=" + filters["Script Title"].replaceAll(" ", "+")
                                  }}>
                                    searching the database
                                  </Link>
                                  {" first!"}
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                      </Col>
                    </Row>

                    {haveTitle && (scriptsFound || filteredRequestCount > 0) && (
                      <Row>
                        <Col>
                          <Form.Check
                            type="checkbox"
                            label="Confirm you've searched and still want to submit a request."
                            checked={searchDone}
                            onChange={(e) => { setSearchDone(e.target.checked); }}
                          />
                        </Col>
                      </Row>
                    )}
                  </Container>
                )}

                {shouldShowForm && (
                  <Form noValidate>
                    <Container className="mw-100 mt-4 mb-4">
                      <Row>
                        <Col>
                          <h5>Submit Your Request</h5>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12} lg={6} xxl={4}>
                          <Form.Group controlId="script_title">
                            <Form.Label className="mt-3">Script Title</Form.Label>
                            <Form.Control
                              type="text"
                              name="script_title"
                              value={filters["Script Title"] ? filters["Script Title"].slice(0, 100) : ""}
                              readOnly
                              className="m-0"
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12} lg={6} xxl={4}>
                          <Form.Group controlId="writers">
                            <Form.Label className="mt-3">Writers</Form.Label>
                            <Form.Control
                              type="text"
                              name="writers"
                              value={formData.writers}
                              maxLength={100}
                              onChange={handleInputChange}
                              className="m-0"
                              placeholder="Names of one or more writers"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12} lg={6} xxl={4}>
                          <Form.Group controlId="additional_info">
                            <Form.Label className="mt-3">Additional information</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              name="additional_info"
                              value={formData.additional_info}
                              maxLength={200}
                              onChange={handleInputChange}
                              className="m-0"
                              placeholder="Version, draft date, etc."
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="mt-4">
                        <Col md={12} lg={6} xxl={4}>
                          <Form.Text>
                            <p>Please note that we only accept requests for <strong>film</strong> and <strong>television</strong> scripts or related materials.</p>
                            <p>We also recommend only requesting <strong>one</strong> script at a time with specific details. This helps our curators prioritize their search.</p>
                            <p>Requests that don't meet our guidelines or are too broad will be removed at our discretion.</p>
                          </Form.Text>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12} lg={6} xxl={4}>
                          <div className="mt-4 mb-5 d-flex gap-2">
                            <Button
                              variant="primary"
                              onClick={handleSubmit}
                              disabled={isSubmitting || !formData.writers.trim() || !formData.requested_email.trim()}
                            >
                              {isSubmitting ? "Submitting..." : "Submit Request"}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              type="button"
                              onClick={resetForm}
                              disabled={isSubmitting}
                            >
                              Reset
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Container>
                  </Form>
                )}

                {userData && !shouldShowForm && (
                  <>
                    {haveAdded.current ? (
                      <>
                        <Container className="mw-100 mt-4">
                          <Row>
                            <Col><h3 className="font-bold">Recently Fulfilled Requests</h3></Col>
                          </Row>
                          <Row>
                            <Col>
                              <p>
                                We're always on the hunt for new additions, and the list below is a small selection of scripts we've added based on requests from our users.
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={12} md={6} className="d-flex justify-content-center justify-content-md-start">
                              <p>
                                Showing <strong>{shownAddedRequests.length}</strong> out of <strong>{addedRequests.length}</strong> requests added to the database.
                              </p>
                            </Col>
                            <Col xs={12} md={6} className="d-flex justify-content-center justify-content-md-end">
                              <PageList
                                totalCount={addedRequests.length}
                                itemsPerPage={ADDED_ITEMS_PER_PAGE}
                                currentPage={currentAddedPage}
                                setCurrentPage={setCurrentAddedPage}
                              />
                            </Col>
                          </Row>
                        </Container>
                        {addedRequests.length > 0 ? (
                          <Container className="mw-100 mt-0 mb-1">
                            <Table
                              striped
                              hover
                              responsive
                              size="sm"
                              className="requests-table"
                            >
                              <thead>
                                <tr>
                                  <th style={{ width: "auto" }}>Script Title</th>
                                  {!isSmall && (
                                    <>
                                      {isMedium && (
                                        <th style={{ width: "auto" }}>Writers</th>
                                      )}
                                      {isLarge && (
                                        <th style={{ width: "auto" }}>Additional Info</th>
                                      )}
                                      {isExtraExtraLarge && (
                                        <th style={{ width: "200px" }}>Date Requested</th>
                                      )}
                                    </>
                                  )}
                                  <th style={{ width: "150px" }}>Date Added</th>
                                </tr>
                              </thead>
                              <tbody>
                                {shownAddedRequests
                                  .map((row, index) => (
                                    <tr key={index}>
                                      <td>
                                        <span title={row.files?.["Script Title"] || row.script_title} className={isExtraExtraLarge ? "requests-text" : ""}>
                                          <Link
                                            to={
                                              row.files?.file_id ?
                                                `/database/script/${row.files.file_id}` :
                                                `/database/?Script+Title=${row.script_title.replaceAll(" ", "+")}${row.writers ? `&Writer(s)=${row.writers.replaceAll(" ", "+")}` : ""}`
                                            }
                                            state={{ from: "requests" }}
                                          >
                                            {row.files?.["Script Title"] || row.script_title}
                                          </Link>
                                        </span>
                                        {!isMedium && (
                                          <small>
                                            {row.files?.["Writer(s)"] && (
                                              <span title={row.files?.["Writer(s)"] || row.writers}><br />{`by ${row.files?.["Writer(s)"] || row.writers}`}</span>
                                            )}
                                          </small>
                                        )}
                                        {!isLarge && row.files?.additional_info_concat && (
                                          <small>
                                            {row.files.additional_info_concat && (
                                              <span title={row.files.additional_info_concat}>
                                                <br />
                                                {row.files.additional_info_concat}
                                              </span>
                                            )}
                                          </small>
                                        )}
                                        {!isExtraExtraLarge && (
                                          <small>
                                            <em>
                                              {row.date_requested && (
                                                <span title={row.date_requested}>
                                                  <br />
                                                  {`Requested ${new Date(row.date_requested).toLocaleDateString()}`}
                                                </span>
                                              )}
                                            </em>
                                          </small>
                                        )}
                                      </td>
                                      {!isSmall && (
                                        <>
                                          {isMedium && (
                                            <td><span title={row.files?.["Writer(s)"] || row.writers} className="requests-text">{row.files?.["Writer(s)"] || row.writers}</span></td>
                                          )}
                                          {isLarge && (
                                            <td><span title={row.files?.additional_info_concat} className="requests-text">{row.files?.additional_info_concat}</span></td>
                                          )}
                                          {isExtraExtraLarge && (
                                            <td>{new Date(row.date_requested).toLocaleDateString()}</td>
                                          )}
                                        </>
                                      )}
                                      <td>{row.files?.["Date Added"] ? new Date(row.files?.["Date Added"]).toLocaleDateString() : ''}</td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </Table>
                          </Container>
                        ) : (
                          <Container className="mw-100">
                            <Row>
                              <Col>
                                <p>Sorry, none of the requests below have been added to the database yet!</p>
                              </Col>
                            </Row>
                          </Container>
                        )}
                      </>
                    ) : (
                      <LoadingRing />
                    )}
                  </>
                )}

                {!shouldShowForm && (
                  <>
                    <Container className="mw-100 my-4">
                      <Row>
                        <Col><h3 className="font-bold">Current Script Requests</h3></Col>
                      </Row>
                      <Row>
                        <Col>
                          <p>The requests below were submitted by our members. If you have any of these scripts or need us to remove one from the list, please drop us a line at <a href="mailto:scripts@scripthive.com" title="scripts@scripthive.com">scripts@scripthive.com</a>!</p>
                        </Col>
                      </Row>
                      <Row>
                        <FilterContext value={{ filters: filters, setFilters: setFilters, search: {} }}>
                          <Col xs={12} md={canAddDelete ? 6 : 4} lg={canAddDelete ? 3 : 4}>
                            <SearchText
                              fieldName="script_title"
                              placeholder="Script Title"
                              disabled={filters && filters["Script Title"] ? true : false}
                            />
                          </Col>
                          <Col xs={12} md={canAddDelete ? 6 : 4} lg={canAddDelete ? 3 : 4}>
                            <SearchText
                              fieldName="writers"
                              placeholder="Writers"
                              disabled={filters && filters["Script Title"] ? true : false}
                            />
                          </Col>
                          <Col xs={12} md={canAddDelete ? 6 : 4} lg={canAddDelete ? 3 : 4}>
                            <SearchText
                              fieldName="additional_info"
                              placeholder="Additional Info"
                              disabled={filters && filters["Script Title"] ? true : false}
                            />
                          </Col>
                          {canAddDelete && (
                            <Col md={6} lg={3}>
                              <SearchText
                                fieldName="requested_email"
                                placeholder="Requester"
                                disabled={filters && filters["Script Title"] ? true : false}
                              />
                            </Col>
                          )}
                        </FilterContext>
                      </Row>
                      <Row>
                        <Col xs={12} xl={4} className="d-flex justify-content-center justify-content-xl-start">
                          <p className="my-1">
                            Showing <strong>{numberFormatter(shownRequests.length)}</strong> out of <strong>{filters ? filteredRequestCount : sortedRequests.length}</strong> pending requests.
                          </p>
                        </Col>
                        <Col xs={12} xl={8} className="d-flex justify-content-center justify-content-xl-end">
                          <Stack direction={isMedium ? "horizontal" : "vertical"} gap={2}>
                            <PageList
                              totalCount={filters ? filteredRequestCount : sortedRequests.length}
                              itemsPerPage={ITEMS_PER_PAGE}
                              currentPage={currentPage}
                              setCurrentPage={setCurrentPage}
                              className="m-0 justify-content-center justify-content-xl-start"
                            />
                          {canAddDelete && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={false}
                              onClick={() => {
                                setShowCheckMatches(true);
                                setMatchesFound([]);
                                fetchMatches(filters ? filteredRequests : sortedRequests);
                              }}
                            >
                              Check for Matches
                            </Button>
                          )}
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={shownRequests.length === 0}
                              onClick={() => {
                                let requestsToExport = filters ? filteredRequests : sortedRequests;
                                requestsToExport = requestsToExport.map((r) => {
                                  return {
                                    "Script Title": r.script_title,
                                    "Writers": r.writers,
                                    "Additional Info": r.additional_info,
                                    "Date Requested": r.date_requested ? new Date(r.date_requested).toLocaleDateString() : '',
                                    [canAddDelete ? "Requester" : ""]: canAddDelete ? r.requested_email : ''
                                  };
                                });
                                exportCsv('script-requests', requestsToExport);
                              }}
                            >
                              Export CSV
                            </Button>
                          </Stack>
                        </Col>
                      </Row>
                    </Container>
                    {filteredRequestCount !== 0 ? (
                      <Container className="mw-100 mt-0 mb-5">
                        <Table
                          striped
                          hover
                          responsive
                          size="sm"
                          className="requests-table"
                        >
                          <thead>
                            <tr>
                              <SortableHeader
                                label="Script Title"
                                sortKey="script_title"
                                sortConfig={sortConfig}
                                onSort={handleSort}
                              />
                              {!isSmall && (
                                <>
                                  {((isExtraLarge && canAddDelete )|| (isMedium && !canAddDelete)) && (
                                    <>
                                      <SortableHeader
                                        label="Writers"
                                        sortKey="writers"
                                        sortConfig={sortConfig}
                                        onSort={handleSort}
                                      />
                                      <SortableHeader
                                        label="Additional Info"
                                        sortKey="additional_info"
                                        sortConfig={sortConfig}
                                        onSort={handleSort}
                                      />
                                    </>
                                  )}
                                  {isExtraExtraLarge && (
                                    <SortableHeader
                                      label="Date Requested"
                                      sortKey="date_requested"
                                      sortConfig={sortConfig}
                                      onSort={handleSort}
                                      width={180}
                                    />
                                  )}
                                </>
                              )}
                              {userData && canAddDelete && (
                                <>
                                  {!isSmall && (
                                    <SortableHeader
                                      label="Requester"
                                      sortKey="requested_email"
                                      sortConfig={sortConfig}
                                      onSort={handleSort}
                                    />
                                  )}
                                  <th style={{ width: isSmall ? "140px" : "200px" }}>Actions</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {shownRequests.map((row, index) => (
                                <tr key={index}>
                                  <td>
                                    <span title={row.script_title} className={isExtraExtraLarge ? "requests-text" : ""}>
                                      {row.script_title}
                                    </span>
                                  {!((isExtraLarge && canAddDelete )|| (isMedium && !canAddDelete)) && (
                                      <>
                                        <small>
                                          {row.writers && (<span title={row.writers}><br />{`by ${row.writers}`}</span>)}
                                        </small>
                                        <small>
                                          {row.additional_info && (<span title={row.additional_info}><br />{row.additional_info}</span>)}
                                        </small>
                                      </>
                                    )}
                                    {!isExtraExtraLarge && (
                                      <small>
                                        <em>
                                          {row.date_requested && (
                                            <span title={row.date_requested}>
                                              <br />
                                              {`Requested ${new Date(row.date_requested).toLocaleDateString()}`}
                                            </span>
                                          )}
                                        </em>
                                      </small>
                                    )}
                                  </td>
                                  {!isSmall && (
                                    <>
                                      {((isExtraLarge && canAddDelete ) || (isMedium && !canAddDelete)) && (
                                        <>
                                          <td><span title={row.writers} className="requests-text">{row.writers}</span></td>
                                          <td><span title={row.additional_info} className="requests-text">{row.additional_info}</span></td>
                                        </>
                                      )}
                                      {isExtraExtraLarge && (
                                        <td>{new Date(row.date_requested).toLocaleDateString()}</td>
                                      )}
                                    </>
                                  )}
                                  {userData && canAddDelete && (
                                    <>
                                      {!isSmall && (
                                        <td><span title={row.requested_email}>{row.requested_email}</span></td>
                                      )}
                                      <td>
                                        <Stack direction={isSmall ? "vertical" : "horizontal"} gap={2} className="p-1">
                                          <Button
                                            variant="outline-success"
                                            size="sm"
                                            onClick={() => {
                                              setScriptTitle(row.script_title);
                                              setScriptWriters(row.writers);
                                              setRequestID(row.id);
                                              setOnAddConfirm(() => () => {
                                                setShowAddModal(false);
                                                fetchPendingRequests().then((data) => {
                                                  if (data) {
                                                    setTotalRequests(data);
                                                  }
                                                });
                                                fetchAddedRequests().then((data) => {
                                                  if (data) {
                                                    const sortedData = sortScripts(data, { key: 'files.Date Added', direction: 'desc' });
                                                    setAddedRequests(sortedData);
                                                  }
                                                });
                                              });
                                              setOnAddHide(() => () => {
                                                setShowAddModal(false);
                                              });
                                              setShowAddModal(true);
                                            }}
                                          >
                                            Mark Added
                                          </Button>
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => {
                                              setConfirmationMessage(`Are you sure you want to delete the request for ${row.script_title}${row.writers ? ` by ${row.writers}` : ''}?\n\nDeleting a request will also prevent new submissions for the same title and writer(s).`);
                                              setOnDeleteConfirm(() => () => {
                                                deleteRequest(row).then(() => {
                                                  fetchPendingRequests().then((data) => {
                                                    if (data) {
                                                      setTotalRequests(data);
                                                    }
                                                  });
                                                });
                                                setShowDeleteModal(false);
                                              });
                                              setOnDeleteHide(() => () => {
                                                setShowDeleteModal(false);
                                              });
                                              setShowDeleteModal(true);
                                            }}
                                          >
                                            Delete
                                          </Button>
                                        </Stack>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      </Container>
                    ): (
                      <Container className="mw-100 mt-0 mb-5">
                        <Row>
                          <Col>
                              <p>No pending requests found. Have you tried searching the <Link to={{ pathname: "/database" }}>database</Link>?</p>
                          </Col>
                        </Row>
                      </Container>
                    )}
                  </>
                )}
              </>
            ) : (
              <LoadingRing />
            )}
          </main>
        </div>
      ) : (
          <div className="mx-1 mx-sm-5">
            <header className="mt-4">
              <Container className="mw-100">
                <Row>
                  <Col><h3 className="font-bold">Check for Matches</h3></Col>
                </Row>
              </Container>
            </header>
            <main>
              <Container className="mw-100 mb-4">
                <Row>
                  <Col>
                    <p>Checking request <strong>{matchProgress.current}</strong> of <strong>{matchProgress.total}</strong> for matches in the database...</p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      variant="outline-secondary mb-4"
                      disabled={isCheckingMatches}
                      onClick={() => {
                        setMatchesFound([]);
                        setShowCheckMatches(false);
                      }}
                    >
                      Back to Requests
                    </Button>
                    <Button
                      variant="warning mb-4 ms-2"
                      disabled={!isCheckingMatches}
                      onClick={() => {
                        isCheckingMatchesRef.current = false;
                        setIsCheckingMatches(false);
                      }}
                    >
                      Stop Checking
                    </Button>

                  </Col>
                </Row>
                <Row>
                  <Col>
                    <ProgressBar
                      animated={isCheckingMatches}
                      now={matchProgress.total > 0 ? (matchProgress.current / matchProgress.total) * 100 : 0}
                    />
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col>
                    {matchesFound.length > 0 ? (
                      <>
                        <p>Found <strong>{matchesFound.length} requests</strong> with potential matches.</p>
                        <Table
                          striped
                          hover
                          responsive
                          size="sm"
                        >
                          <thead>
                            <tr>
                              <th style={{ width: "300px" }}>Request</th>
                              {!isSmall && (<th>Potential Matches</th>)}
                              <th style={{ width: isSmall? "150px" : "200px" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {matchesFound.map((row, index) => (
                              <tr key={index}>
                                <td>
                                  <strong>{row.script_title}</strong>
                                  {row.writers ? ` by ${row.writers}` : ''}<br/>
                                  {row.additional_info ? <>{row.additional_info}<br/></> : ''}
                                  {row.date_requested ? `Requested ${new Date(row.date_requested).toLocaleDateString()}` : ''}
                                  {isSmall && (
                                    <>
                                      <br/><br/>
                                      <em>Found <strong>{row.matches.length}</strong> potential matches</em>
                                    </>
                                  )}
                                </td>
                                {!isSmall && (<td>
                                  {row.matches.map((match, matchIndex) => {
                                    console.log(match);
                                    return(
                                      <div key={matchIndex} >
                                        <Link
                                          to={`/database/script/${match.file_id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {match.script_title}
                                        </Link>
                                        {match.writers ? ` by ${match.writers}` : ''}
                                        {match.additional_info_concat ? `, ${match.additional_info_concat}` : ''}
                                        {match.date_added ? `, Added ${new Date(match.date_added).toLocaleDateString()}, ` : ''}
                                        {match.match_score ?
                                          <span
                                            style={{
                                              fontWeight: 'bold',
                                              color: match.match_score >= 0.8 ? 'green' : 'inherit'
                                            }}
                                          >
                                            {Math.round(match.match_score * 100)}% match
                                          </span>
                                          : ''
                                        }
                                      </div>
                                    )
                                  })}
                                </td>)}
                                <td>
                                  <Stack direction={isSmall ? "vertical" : "horizontal"} gap={2} className="p-1">
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      disabled={isCheckingMatches}
                                      onClick={() => {
                                        setScriptTitle(row.script_title);
                                        setScriptWriters(row.writers);
                                        setRequestID(row.id);
                                        setOnAddConfirm(() => () => {
                                          setShowAddModal(false);
                                          fetchPendingRequests().then((data) => {
                                            if (data) {
                                              setTotalRequests(data);
                                            }
                                          });
                                          fetchAddedRequests().then((data) => {
                                            if (data) {
                                              const sortedData = sortScripts(data, { key: 'files.Date Added', direction: 'desc' });
                                              setAddedRequests(sortedData);
                                            }
                                          });
                                          setMatchesFound((prevMatches) => prevMatches.filter((r) => r.id !== row.id));
                                        });
                                        setOnAddHide(() => () => {
                                          setShowAddModal(false);
                                        });
                                        setShowAddModal(true);
                                      }}
                                    >
                                      Mark Added
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      disabled={isCheckingMatches}
                                      onClick={() => {
                                        setConfirmationMessage(`Are you sure you want to delete the request for ${row.script_title}${row.writers ? ` by ${row.writers}` : ''}?\n\nDeleting a request will also prevent new submissions for the same title and writer(s).`);
                                        setOnDeleteConfirm(() => () => {
                                          deleteRequest(row).then(() => {
                                            fetchPendingRequests().then((data) => {
                                              if (data) {
                                                setTotalRequests(data);
                                                setMatchesFound((prevMatches) => prevMatches.filter((r) => r.id !== row.id));
                                              }
                                            });
                                          });
                                          setShowDeleteModal(false);
                                        });
                                        setOnDeleteHide(() => () => {
                                          setShowDeleteModal(false);
                                        });
                                        setShowDeleteModal(true);
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    ) : (
                        !isCheckingMatches && (<p>No matches found.</p>)
                    )}
                  </Col>
                </Row>
              </Container>
            </main>
          </div>
      )}
      <ConfirmationModal show={showDeleteModal} message={confirmationMessage} onConfirm={onDeleteConfirm} onHide={onDeleteHide} />
      <AddConfirmationModal id={requestID} show={showAddModal} title={scriptTitle} writers={scriptWriters} onConfirm={onAddConfirm} onHide={onAddHide} />
    </>
  );
}

export default RequestsPage;